/**
 * Target brackets.
 *
 * Only what you are actually looking at gets named. A name floating over every
 * body at once is a wall of text with nothing tying each line to its object; a
 * bracket is anchored, and restricting it to the middle of the screen makes the
 * HUD answer "what is this" rather than narrate the system.
 *
 * Written to the DOM rather than into the scene. These are flat, screen-aligned
 * and want real text rendering, and sprite text at this many bodies is a texture
 * atlas problem for no gain.
 *
 * Lives here rather than inside a view because both the system map and flight
 * want it, and they want it to look the same — a bracket that meant one thing on
 * the chart and another out of the window would be worse than none.
 */

import * as THREE from 'three';

import type { MarkerKind } from './systemScene';

export interface MarkerTarget {
  text: string;
  kind: MarkerKind;
  node: THREE.Object3D;
  /** World-space radius, so the bracket can be sized to the object. */
  radius: number;
}

export interface MarkerUpdate {
  camera: THREE.Camera;
  width: number;
  height: number;
  /**
   * Where distances are measured from — the ship, in flight.
   *
   * Omitted on the chart, where "how far from the camera" is a fact about where
   * you are scrolled to rather than about the world.
   */
  from?: THREE.Vector3 | null;
  /**
   * Screen point that decides which single marker closes its bracket.
   *
   * Only one may close, and it is whichever the caller would actually capture —
   * closing every marker in range would promise several targets and deliver one.
   */
  pivot?: { x: number; y: number; within: number } | null;
  /**
   * Current speed, for working out how long the trip would take.
   *
   * A distance alone does not tell you whether something is worth flying to.
   * Seventeen thousand units is nothing under warp and three minutes on
   * thrusters, and the number that answers the question is the time.
   */
  speed?: number;
  /**
   * The target cruise is locked onto, if any.
   *
   * Its bracket becomes a ring: the autopilot is flying the ship, and a control
   * surface that has been handed over should not look like one that has not.
   */
  locked?: string | null;
  /**
   * Bodies that can stand in the way, with world-space radii.
   *
   * A bracket is a claim that you can see the thing it names. Without this the
   * claim was false surprisingly often: a moon on the far side of its planet
   * still drew its name and its distance right over the planet's face, and the
   * marker for something behind a star sat in the middle of the star.
   *
   * The list is the same set of spheres collision already tracks, so nothing
   * new is generated to support it.
   */
  occluders?: readonly { node: THREE.Object3D; radius: number }[];
  /**
   * The body that has been pinned as a destination, by name.
   *
   * Drawn in the route colour and never occluded away: the whole point of
   * pinning something is to be able to find it, and a bracket that vanishes
   * because a moon drifted in front of it is answering a different question
   * from the one being asked.
   */
  routed?: string | null;
}

export interface TargetMarkers {
  update: (options: MarkerUpdate) => void;
  dispose: () => void;
}

/**
 * Bracket corner length, as a fraction of the box.
 *
 * At a half the four corners meet and the bracket becomes a closed square,
 * which is how a marker says it is within snapping range. Same shape growing
 * into itself rather than a different marker swapped in, so the change reads as
 * one thing tightening.
 */
const CORNER = 0.28;
const CORNER_LOCKED = 0.5;

/** Fraction of the screen, centred, inside which a body is called out. */
const MARKER_ZONE = 0.5;

/**
 * The one colour that means "this is where you are going".
 *
 * Shared with the jump markers, deliberately: the sky and the system are two
 * halves of one question — where is my destination — and answering them in
 * different colours would make the player learn it twice.
 */
export const ROUTE_COLOUR = 'rgba(190, 120, 255, 0.98)';

const colourOf = (marker: { target: MarkerTarget; routed?: boolean }): string =>
  marker.routed ? ROUTE_COLOUR : (MARKER_COLORS[marker.target.kind] ?? 'rgba(215,232,255,0.85)');

const MARKER_COLORS: Record<MarkerKind, string> = {
  // Its own colour rather than a planet's: a star is the one thing in a system
  // you navigate *by* as well as to, and it should not read as another rock.
  star: 'rgba(255, 236, 170, 0.9)',
  planet: 'rgba(150, 200, 255, 0.85)',
  moon: 'rgba(170, 180, 200, 0.7)',
  station: 'rgba(255, 212, 121, 0.9)',
  anomaly: 'rgba(201, 139, 255, 0.85)',
};

/**
 * How long this would take at this speed, in the shortest useful form.
 *
 * Nothing below about a unit a second counts as travelling: a ship drifting at
 * a tenth of a unit yields an ETA in days, which is technically true and tells
 * you only that you are not moving.
 */
export function formatEta(distance: number, speed: number): string | null {
  if (speed < 1 || distance <= 0) return null;

  const seconds = distance / speed;
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  return `${(seconds / 3600).toFixed(1)}h`;
}

/**
 * A distance, in the shortest form that still says something.
 *
 * Three significant figures throughout: at these scales the exact metre is
 * never the point, and a fourteen-digit readout that changes every frame is
 * unreadable in a way a rounded one is not.
 */
export function formatDistance(units: number): string {
  const distance = Math.abs(units);

  if (distance < 1_000) return `${Math.round(distance)} u`;

  const [divisor, suffix] = distance < 1_000_000 ? [1_000, 'ku'] : [1_000_000, 'Mu'];
  const value = distance / divisor;

  // Three significant figures whatever the magnitude, so the readout keeps a
  // steady width instead of the label shuffling as you close.
  const places = value < 10 ? 2 : value < 100 ? 1 : 0;
  return `${value.toFixed(places)} ${suffix}`;
}

/**
 * Whether a sphere stands between the eye and a point.
 *
 * Ordinary ray-versus-sphere, with the two edge cases that matter here:
 *
 * - An occluder the eye is *inside* blocks nothing. Being within a body's
 *   radius means it is around you, not in front of you, and treating it as an
 *   occluder would blank every marker on the screen at once.
 * - Anything at or beyond the target is not in the way. Without the near-point
 *   allowance a body would occlude *itself*, since its own centre is exactly
 *   one radius past its surface.
 */
export function sightBlocked(
  eye: THREE.Vector3,
  target: THREE.Vector3,
  targetRadius: number,
  occluder: THREE.Vector3,
  occluderRadius: number,
): boolean {
  const toTarget = target.clone().sub(eye);
  const reach = toTarget.length();
  if (reach === 0) return false;

  const toOccluder = occluder.clone().sub(eye);
  if (toOccluder.length() <= occluderRadius) return false;

  toTarget.divideScalar(reach);

  // How far along the sight line the occluder's centre sits.
  const along = toOccluder.dot(toTarget);

  // Behind the eye, or not before the target's near face.
  if (along <= 0 || along >= reach - targetRadius) return false;

  // Perpendicular miss distance: inside the radius and the sphere covers it.
  const miss = toOccluder.clone().addScaledVector(toTarget, -along).length();
  return miss < occluderRadius;
}

export function createTargetMarkers(
  layer: HTMLElement,
  targets: readonly MarkerTarget[],
): TargetMarkers {
  layer.replaceChildren();

  const markers = targets.map((target) => {
    const colour = MARKER_COLORS[target.kind] ?? 'rgba(215,232,255,0.85)';

    const root = document.createElement('div');
    root.style.cssText =
      'position:absolute;pointer-events:none;transform:translate(-50%,-50%);display:none';

    // Four corners rather than a closed rectangle: a full box reads as a window
    // frame and hides the thing inside it.
    const box = document.createElement('div');
    box.style.cssText = 'position:absolute;inset:0';
    const corners: HTMLDivElement[] = [];

    for (const [x, y] of [[0, 0], [1, 0], [0, 1], [1, 1]] as const) {
      const corner = document.createElement('div');
      corner.style.cssText = [
        'position:absolute',
        `${y ? 'bottom' : 'top'}:0`,
        `${x ? 'right' : 'left'}:0`,
        `width:${CORNER * 100}%`,
        `height:${CORNER * 100}%`,
        `border-${y ? 'bottom' : 'top'}:1px solid ${colour}`,
        `border-${x ? 'right' : 'left'}:1px solid ${colour}`,
        'transition:width 90ms linear,height 90ms linear',
      ].join(';');
      box.appendChild(corner);
      corners.push(corner);
    }
    root.appendChild(box);

    const name = document.createElement('div');
    name.textContent = target.text;
    name.style.cssText = `position:absolute;bottom:100%;left:50%;transform:translateX(-50%);margin-bottom:5px;font-size:11px;white-space:nowrap;text-shadow:0 1px 3px #05070d;color:${colour}`;
    root.appendChild(name);

    // Below the box, so the name stays put as the number changes width. Sharing
    // a line with the name had the whole label shuffling sideways every tenth of
    // a second while closing on something.
    const range = document.createElement('div');
    range.style.cssText = `position:absolute;top:100%;left:50%;transform:translateX(-50%);margin-top:5px;font-size:10px;white-space:nowrap;font-variant-numeric:tabular-nums;text-shadow:0 1px 3px #05070d;opacity:0.72;display:none;color:${colour}`;
    root.appendChild(range);

    layer.appendChild(root);
    return {
      root,
      box,
      corners,
      range,
      name,
      closed: false,
      ringed: false,
      routed: false,
      shown: '',
      target,
    };
  });

  const world = new THREE.Vector3();
  const edge = new THREE.Vector3();
  const centre = new THREE.Vector3();

  const eye = new THREE.Vector3();
  const occluderAt = new THREE.Vector3();

  const update = ({
    camera,
    width,
    height,
    from,
    pivot,
    speed,
    locked,
    occluders,
    routed = null,
  }: MarkerUpdate): void => {
    camera.getWorldPosition(eye);

    let closest: (typeof markers)[number] | null = null;
    let closestDistance = pivot ? pivot.within : 0;

    const placed: {
      marker: (typeof markers)[number];
      x: number;
      y: number;
      size: number;
      range: number | null;
    }[] = [];

    for (const marker of markers) {
      const { root, target } = marker;
      target.node.getWorldPosition(world);
      centre.copy(world);

      // The object's on-screen size, from projecting a point on its rim as well
      // as its centre. Projecting the radius directly would ignore perspective,
      // and a bracket sized in world units grows without bound as you close in.
      edge.copy(world).addScaledVector(camera.up, target.radius);

      world.project(camera);
      edge.project(camera);

      // Behind the camera, or outside the centred band it calls out in.
      if (world.z > 1 || Math.abs(world.x) > MARKER_ZONE || Math.abs(world.y) > MARKER_ZONE) {
        root.style.display = 'none';
        continue;
      }

      // Repainted when the destination changes. Cheap, and it has to happen
      // before the occlusion check, which the pinned body is exempt from.
      const isRouted = routed !== null && target.text === routed;
      if (isRouted !== marker.routed) {
        marker.routed = isRouted;

        const colour = colourOf(marker);
        marker.name.style.color = colour;
        marker.range.style.color = colour;
        for (const corner of marker.corners) {
          corner.style.borderColor = colour;
        }
      }

      // Something in the way. Tested only for markers that have already passed
      // the zone check, so this is a handful of bodies rather than all of them.
      // The pinned destination is exempt — see `routed`.
      if (!isRouted && occluders && occluders.length > 0) {
        let hidden = false;

        for (const occluder of occluders) {
          if (occluder.node === target.node) continue;

          occluder.node.getWorldPosition(occluderAt);
          if (sightBlocked(eye, centre, target.radius, occluderAt, occluder.radius)) {
            hidden = true;
            break;
          }
        }

        if (hidden) {
          root.style.display = 'none';
          continue;
        }
      }

      const x = (world.x * 0.5 + 0.5) * width;
      const y = (-world.y * 0.5 + 0.5) * height;
      // Floors and ceilings: a bracket must never shrink to nothing on a distant
      // moon, nor grow past the viewport on a planet you are inside.
      const size = THREE.MathUtils.clamp(
        Math.abs(edge.y - world.y) * height * 1.6,
        22,
        Math.min(width, height) * 0.6,
      );

      if (pivot) {
        const offset = Math.hypot(x - pivot.x, y - pivot.y);
        if (offset < closestDistance) {
          closestDistance = offset;
          closest = marker;
        }
      }

      // Surface distance, not centre distance. Sitting a hundred units off a
      // planet and being told you are twelve hundred from it is the kind of
      // number that makes a HUD untrustworthy.
      const range = from
        ? Math.max(0, centre.distanceTo(from) - target.radius)
        : null;

      placed.push({ marker, x, y, size, range });
    }

    for (const { marker, x, y, size, range } of placed) {
      const { root, corners } = marker;

      root.style.display = '';
      root.style.left = `${x}px`;
      root.style.top = `${y}px`;
      root.style.width = `${size}px`;
      root.style.height = `${size}px`;

      // Only touch the DOM when the text actually changes. This runs for every
      // marker every frame, and the string is the same for most of them.
      const eta = range === null || speed === undefined ? null : formatEta(range, speed);
      const text = range === null ? '' : formatDistance(range) + (eta ? ` · ${eta}` : '');
      if (text !== marker.shown) {
        marker.shown = text;
        marker.range.textContent = text;
        marker.range.style.display = text ? '' : 'none';
      }

      // Under cruise the bracket becomes a ring. The autopilot has the ship, and
      // a target it is flying to should not look like one you are merely
      // pointing at.
      const ringed = locked !== undefined && locked !== null && marker.target.text === locked;
      if (ringed !== marker.ringed) {
        marker.ringed = ringed;
        marker.box.style.borderRadius = ringed ? '50%' : '';
        marker.box.style.border = ringed ? `1px solid ${colourOf(marker)}` : 'none';
        for (const corner of marker.corners) corner.style.opacity = ringed ? '0' : '1';
      }

      const closed = marker === closest;
      if (closed === marker.closed) continue;
      marker.closed = closed;

      // Closed, the four corners meet and the bracket is a whole square. That is
      // what "this is the one" looks like — the same shape tightening into
      // itself rather than a different marker swapped in.
      const extent = `${(closed ? CORNER_LOCKED : CORNER) * 100}%`;
      for (const corner of corners) {
        corner.style.width = extent;
        corner.style.height = extent;
      }
    }
  };

  return { update, dispose: () => layer.replaceChildren() };
}
